import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

let quillPromise = null;
function loadQuill() {
  if (window.Quill) return Promise.resolve(window.Quill);
  if (quillPromise) return quillPromise;
  quillPromise = new Promise((resolve, reject) => {
    if (!document.querySelector('link[data-camp-quill="1"]')) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://cdn.jsdelivr.net/npm/quill@2.0.3/dist/quill.snow.css";
      link.dataset.campQuill = "1";
      document.head.appendChild(link);
    }
    const existing = document.querySelector('script[data-camp-quill="1"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(window.Quill));
      existing.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://cdn.jsdelivr.net/npm/quill@2.0.3/dist/quill.js";
    script.async = true;
    script.dataset.campQuill = "1";
    script.onload = () => resolve(window.Quill);
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return quillPromise;
}

const FieldNoteRichEditor = forwardRef(function FieldNoteRichEditor(
  { value, onChange, disabled = false, small = false, placeholder = "Write here…" },
  ref
) {
  const hostRef = useRef(null);
  const quillRef = useRef(null);
  const initialValueRef = useRef(value || "<p><br></p>");

  useEffect(() => {
    let active = true;
    loadQuill().then((Quill) => {
      if (!active || !hostRef.current || quillRef.current) return;
      const quill = new Quill(hostRef.current, {
        theme: "snow",
        placeholder,
        modules: {
          toolbar: disabled
            ? false
            : [
                [{ header: [2, 3, false] }],
                ["bold", "italic", "underline"],
                [{ list: "ordered" }, { list: "bullet" }],
                ["blockquote", "link"],
                ["clean"],
              ],
        },
      });
      quill.root.innerHTML = initialValueRef.current || "<p><br></p>";
      quill.enable(!disabled);
      quill.on("text-change", () => onChange && onChange(quill.root.innerHTML));
      quillRef.current = quill;
    });
    return () => {
      active = false;
      if (quillRef.current) quillRef.current.off("text-change");
    };
  }, []); // intentionally initialise once for this loaded document

  useEffect(() => {
    if (quillRef.current) quillRef.current.enable(!disabled);
  }, [disabled]);

  useImperativeHandle(ref, () => ({
    insertMarker(blockKey) {
      const q = quillRef.current;
      if (!q || disabled) return;
      const marker = `[[FIELDNOTE_BLOCK:${blockKey}]]`;
      const range = q.getSelection(true);
      const index = range ? range.index : Math.max(0, q.getLength() - 1);
      q.insertText(index, `\n${marker}\n`, "user");
      q.setSelection(index + marker.length + 2, 0, "silent");
    },
    removeMarker(blockKey) {
      const q = quillRef.current;
      if (!q || disabled) return;
      const marker = `[[FIELDNOTE_BLOCK:${blockKey}]]`;
      const text = q.getText();
      const index = text.indexOf(marker);
      if (index >= 0) q.deleteText(index, marker.length, "user");
    },
    getHtml() {
      return quillRef.current ? quillRef.current.root.innerHTML : value;
    },
  }));

  return <div className={`fieldnote-writer-editor ${small ? "fieldnote-writer-editor--small" : ""}`} ref={hostRef} />;
});

export default FieldNoteRichEditor;
